import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import { isLoginExemptPath } from '../../utils/publicAccessPaths';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const POLL_INTERVAL_MS = 45000; // Re-check when admin toggles "login required"

/**
 * When config "Login required" is ON and user is not logged in (or is guest),
 * redirect to /login with state.from so they can return after login.
 */
const LoginRequiredGuard = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuthContext();
  const [loginRequired, setLoginRequired] = useState(false);
  const [guardReady, setGuardReady] = useState(false);

  const fetchLoginRequired = () => {
    return fetch(`${API_BASE}/api/settings/public/login-required`)
      .then((res) => res.json())
      .then((data) => !!data.loginRequired)
      .catch(() => false);
  };

  // Initial fetch + poll so we react when admin enables "login required" on any URL
  useEffect(() => {
    let cancelled = false;
    fetchLoginRequired()
      .then((value) => {
        if (!cancelled) {
          setLoginRequired(value);
          setGuardReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoginRequired(false);
          setGuardReady(true);
        }
      });
    const interval = setInterval(() => {
      fetchLoginRequired().then((value) => {
        if (!cancelled) setLoginRequired(value);
      });
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (!guardReady || isLoading) return;
    if (!loginRequired) return;
    if (isLoginExemptPath(location.pathname)) return;

    // When "Login required" is ON, allow both registered and guest users
    // `isAuthenticated` is true for both registered and guest sessions
    if (isAuthenticated) return;

    const from = location.pathname + location.search;
    navigate('/login', { replace: true, state: { from } });
  }, [guardReady, isLoading, loginRequired, isAuthenticated, location.pathname, location.search, navigate]);

  return <>{children}</>;
};

export default LoginRequiredGuard;
